/**
 * MEDICAL DEMO ENTRIES — three no-login personas, one per tab.
 *
 * Mirrors the automotive demo entries exactly: dark theme forced immediately
 * (no white flash on a projector), per-tab demo session via lib/session, a
 * VISIBLE error card on failure (never a silent /login eject), and the persona
 * recorded in sessionStorage so an expired token is re-minted silently instead
 * of ejecting the presenter.
 */
import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { setDemoSession } from '../lib/session';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
export const MED_DEMO_ORG = 'med-demo-org-0001';
// Read by the api interceptor to re-mint silently when a demo token expires.
export const MED_PERSONA_KEY = 'yulontap_med_demo_persona';

const PERSONAS = {
    coordinator: { path: 'coordinator', land: `/command-center?org=${MED_DEMO_ORG}` },
    'head-office': { path: 'head-office', land: `/command-center?org=${MED_DEMO_ORG}` },
};

function Shell({ error, retry, t }) {
    if (!error) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" data-testid="demo-med-loading" />
            </div>
        );
    }
    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
            <div className="max-w-sm w-full rounded-2xl border border-rose-500/30 bg-rose-500/10 p-5 text-center"
                data-testid="demo-med-error">
                <AlertTriangle className="w-6 h-6 text-rose-400 mx-auto mb-3" />
                <p className="text-sm text-white mb-4">{error}</p>
                <button onClick={retry}
                    className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/15"
                    data-testid="demo-med-retry">
                    <RefreshCw className="w-4 h-4" />{t('common.retry', 'Try again')}
                </button>
            </div>
        </div>
    );
}

/** Patient: the real guest flow, no token at all. */
export function DemoMedPatientEntry() {
    const { t } = useTranslation();
    const [error, setError] = useState(null);

    const enter = async () => {
        setError(null);
        try {
            const res = await fetch(`${API}/api/demo-med/patient/info`);
            const d = await res.json().catch(() => ({}));
            if (!res.ok || !d.card_id) throw new Error(`HTTP ${res.status}`);
            sessionStorage.setItem('yulontap_demo_mode', '1');
            window.location.replace(`/review-shield/${d.card_id}`);
        } catch (e) {
            setError(e.message || 'Network error');
        }
    };
    useEffect(() => { enter(); /* eslint-disable-next-line */ }, []);
    return <Shell error={error} retry={enter} t={t} />;
}

/** Staff personas: coordinator (one clinic) and head office (both clinics). */
export function DemoMedStaffEntry({ persona }) {
    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const cfg = PERSONAS[persona];

    const enter = async () => {
        setError(null);
        try { localStorage.setItem('yulontap-theme-preference', 'dark'); } catch (e) { /* private mode */ }
        document.documentElement.classList.add('dark');
        try {
            const params = new URLSearchParams(window.location.search);
            const raw = params.get('return_url') || '';
            const returnUrl = raw.startsWith('/') && !raw.startsWith('//') ? raw : '';
            const qs = returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : '';
            const res = await fetch(`${API}/api/demo-med/${cfg.path}/session${qs}`);
            const d = await res.json().catch(() => ({}));
            if (!res.ok || !d.access_token) {
                throw new Error(`HTTP ${res.status}${d.detail ? ` — ${d.detail}` : ''}`);
            }
            setDemoSession(d.access_token, d.user || {});
            sessionStorage.setItem('yulontap_demo_mode', '1');
            sessionStorage.setItem(MED_PERSONA_KEY, cfg.path);
            if (returnUrl) sessionStorage.setItem('yulontap_demo_return', returnUrl);
            else sessionStorage.removeItem('yulontap_demo_return');
            window.location.replace(cfg.land);
        } catch (e) {
            setError(e.message || 'Network error');
        }
    };
    useEffect(() => { enter(); /* eslint-disable-next-line */ }, [persona]);
    return <Shell error={error} retry={enter} t={t} />;
}

export default DemoMedPatientEntry;
